import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import PageState from '../components/PageState.jsx';
import SectionCard from '../components/SectionCard.jsx';
import { api } from '../lib/api.js';
import ConsoleTabs, { panelProps } from '../components/ConsoleTabs.jsx';
import { useTabParam } from '../lib/use-tab-param.js';
import { tabOfHash, tocGroupsFor } from '../lib/data-reference-tabs.js';
import SourceCard from './DataReferenceSource.jsx';
import DataReferenceToc, { buildTocGroups } from './DataReferenceToc.jsx';
import { Card, Bullets, Term, Desc, Pair, Mono, Code } from './dataReferenceParts.jsx';

/**
 * Developer data reference: what onGuard writes to the customer's S3 bucket
 * and how to read it.
 *
 * The content comes from GET /data-reference (portal/shared/data-catalog.mjs
 * on the server) and is never bundled into this SPA, whose assets are public:
 * only signed-in operators and customer admins can read it.
 *
 * The customer comes from the route (/customers/:customerId/data-reference);
 * /data-reference is the generic reference with placeholders, for operators
 * (pages/LegacyRedirect.jsx DataReferenceRoute). Under the customer header
 * the title is an h2 (embedded).
 *
 * ENTRAID-347 redesign (plan C7): every block is a SectionCard; loading and
 * error use PageState (the error under the page title, with Retry). The page
 * is read-only for every role that can open it, the developer role included.
 */
const INTRO = 'Everything onGuard delivers to your Amazon S3 bucket: where each source lands, how and when it is produced, its format and fields, and what to watch out for when you integrate it.';

export default function DataReference({ embedded = false }) {
  const { customerId = null } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const Title = embedded ? 'h2' : 'h1';

  const [ref, setRef] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRef(null);
    setError(null);
    api.getDataReference(customerId)
      .then((res) => { if (!cancelled) setRef(res.reference); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the data reference.'); });
    return () => { cancelled = true; };
  }, [customerId, attempt]);

  const [tab, setTab] = useTabParam(['guide', 'naming', 'mapping', 'sources'], 'guide');
  const tabRef = useRef(tab);
  tabRef.current = tab;
  // A deep link (#source-<key>, #field-naming, ...) opens the tab that holds it, also when the hash changes in the page.
  // It reacts to the hash only, so choosing another tab while a hash is in the URL is not undone. The tab goes into
  // the tab search param (a reload keeps it) and the hash stays, which setTab would drop.
  useEffect(() => {
    const open = () => {
      const hash = window.location.hash || location.hash;
      const want = tabOfHash(hash);
      if (!want || want === tabRef.current) return;
      const params = new URLSearchParams(location.search);
      if (want === 'guide') params.delete('tab'); else params.set('tab', want);
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash }, { replace: true });
    };
    open();
    window.addEventListener('hashchange', open);
    return () => window.removeEventListener('hashchange', open);
  }, [location.hash, location.pathname, location.search, navigate]);

  // Deep links from the Data Sources hints (#source-<key>): the target only
  // exists once the reference has loaded and its tab has rendered, so scroll after render.
  useEffect(() => {
    if (!ref || !location.hash) return;
    let id = location.hash.slice(1);
    try { id = decodeURIComponent(id); } catch { /* a malformed escape: use the raw text, like tabOfHash */ }
    document.getElementById(id)?.scrollIntoView?.({ block: 'start' });
  }, [ref, location.hash, tab]);

  const header = embedded ? (
    <header className="space-y-2">
      <Title>Data reference</Title>
      <p className="max-w-3xl text-ipc-muted">{INTRO}</p>
    </header>
  ) : <PageHeader title="Data reference" subline={INTRO} />;

  if (error) {
    return (
      <div className="min-w-0 space-y-6">
        {header}
        <PageState state="error" title="The data reference could not be loaded" message={error} onRetry={() => setAttempt((n) => n + 1)} />
      </div>
    );
  }
  if (!ref) return <PageState state="loading" cards={0} label="Loading the data reference" />;

  const tocGroups = tocGroupsFor(tab, buildTocGroups(ref));

  return (
    <div className="min-w-0">
      {header}

      <ConsoleTabs
        label="Data reference"
        idPrefix="data-reference"
        items={[{ id: 'guide', label: 'Guide' }, { id: 'naming', label: 'Field naming' }, { id: 'mapping', label: 'Field mapping' }, { id: 'sources', label: 'Sources' }]}
        value={tab}
        onChange={setTab}
      />

      <div className={tocGroups.length > 0 ? 'mt-4 lg:mt-6 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-8' : 'mt-4 lg:mt-6'}>
        {tocGroups.length > 0 ? <DataReferenceToc groups={tocGroups} /> : null}
        <div {...panelProps('data-reference', tab)} className="mt-6 space-y-8 min-w-0 lg:mt-0">
          {tab === 'guide' ? (
            <>
              <HowToRead overview={ref.overview} bucket={ref.bucket} />


              <Card id="where-data-lives" title="Where the data lives">
                <dl className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                  <Term>Bucket</Term>
                  <Desc>
                    <Mono>{ref.bucket.name}</Mono>
                    {!ref.bucket.known && (
                      <span className="block text-ipc-muted mt-0.5">
                        Your bucket is named <Mono>{ref.bucket.namePattern}</Mono>.
                      </span>
                    )}
                  </Desc>
                  <Term>Region</Term>
                  <Desc><Mono>{ref.region.name}</Mono></Desc>
                  <Term>Schedule</Term>
                  <Desc>{ref.schedule.label}. {ref.schedule.detail}</Desc>
                </dl>
                <div>
                  <h3 className="mb-2">Bucket layout</h3>
                  <ul className="space-y-3">
                    {ref.categories.map((cat) => (
                      <li key={cat.id}>
                        <div className="text-sm font-medium text-ipc-ink">{cat.title}</div>
                        <ul className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                          {cat.sources.map((s) => (
                            <li key={s.key} className="text-sm min-w-0">
                              <a href={`#${s.anchor}`} className="text-ipc-light underline hover:no-underline">{s.label}</a>
                              <span className="text-ipc-muted">: </span>
                              <Mono>{s.prefix}</Mono>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>

              <Card id="file-conventions" title="File conventions">
                <Bullets items={ref.fileConventions} />
                <h3>How each source is produced</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-[12rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                  {Object.entries(ref.productionModes).map(([id, m]) => (
                    <Pair key={id} term={m.label}>
                      {m.description} File date: {m.dateInName}
                    </Pair>
                  ))}
                </dl>
                <h3>_latest.json and tombstones</h3>
                <Bullets items={ref.latestConvention} />
                <h3>Runs, failures, and limits</h3>
                <Bullets items={ref.runBehavior} />
                {ref.licensing?.length > 0 && (
                  <>
                    <h3>Microsoft Entra ID licenses</h3>
                    <Bullets items={ref.licensing} />
                  </>
                )}
              </Card>

              <Card id="retention-security" title="Retention and security">
                <Bullets items={ref.lifecycle} />
                <Bullets items={ref.security} />
              </Card>

              <Card id="run-files" title="Run files">
                <dl className="grid grid-cols-1 gap-y-3 text-sm">
                  {ref.systemFiles.map((f) => (
                    <div key={f.path} className="min-w-0">
                      <dt><Mono>{f.path}</Mono></dt>
                      <dd className="text-ipc-ink mt-0.5">{f.description}</dd>
                    </div>
                  ))}
                </dl>
              </Card>

              <Card id="access-data" title="Access the data">
                <p className="text-sm text-ipc-ink">{ref.access.prerequisites}</p>

                <h3>{ref.access.awsCli.title}</h3>
                <ol className="space-y-2 text-sm text-ipc-ink">
                  {ref.access.awsCli.steps.map((step, i) => (
                    <li key={step} className="min-w-0">
                      {step}
                      <Code>{ref.access.awsCli.commands[i]}</Code>
                    </li>
                  ))}
                </ol>

                <h3>{ref.access.athena.title}</h3>
                <Bullets items={ref.access.athena.notes} />
                {ref.access.athena.sql.map((sql) => <Code key={sql}>{sql}</Code>)}

                <h3>{ref.access.qlik.title}</h3>
                <Bullets items={ref.access.qlik.notes} />
                {ref.access.qlik.script.map((s) => <Code key={s}>{s}</Code>)}
              </Card>

            </>
          ) : null}
          {tab === 'naming' ? <FieldNaming schema={ref.schema} /> : null}
          {tab === 'mapping' ? <FieldMapping schema={ref.schema} /> : null}
          {tab === 'sources' ? ref.categories.map((cat) => (
            <section key={cat.id} className="space-y-4 scroll-mt-16 lg:scroll-mt-4" aria-labelledby={`cat-${cat.id}`}>
              <div>
                <h2 id={`cat-${cat.id}`}>{cat.title}</h2>
                <p className="text-sm text-ipc-muted">{cat.description}</p>
              </div>
              {cat.sources.map((s) => <SourceCard key={s.key} source={s} />)}
            </section>
          )) : null}
        </div>
      </div>
    </div>
  );
}

const CHANGE_LABEL = { renamed: 'Renamed', unchanged: 'Unchanged', new: 'New', removed: 'Removed' };

// The naming convention, the keys that link sources, and the loops a
// single all-sources model would close. Shown right after "How to read this
// data": every field name on the page follows it.
function FieldNaming({ schema }) {
  return (
    <SectionCard id="field-naming" title={`Field naming (schema v${schema.version})`} className="scroll-mt-16 lg:scroll-mt-4" bodyClassName="space-y-4">
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-ipc-ink">
        {schema.convention.map((rule) => <li key={rule}>{rule}</li>)}
      </ol>
      <p className="text-sm text-ipc-ink">{schema.history}</p>
      <div>
        <h3 className="mb-2">Keys shared between sources</h3>
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-ipc-muted border-b border-ipc-border">
                <th scope="col" className="py-1.5 pr-4 font-medium">Field</th>
                <th scope="col" className="py-1.5 pr-4 font-medium">Key of</th>
                <th scope="col" className="py-1.5 font-medium">Carried by</th>
              </tr>
            </thead>
            <tbody>
              {schema.sharedNames.map((s) => (
                <tr key={s.field} className="border-b border-ipc-border align-top">
                  <td className="py-1.5 pr-4"><Mono>{s.field}</Mono></td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{s.owner.label}</td>
                  <td className="py-1.5 text-ipc-ink">{s.referencedBy.map((r) => r.label).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {schema.circularPaths.length > 0 && (
        <div>
          <h3 className="mb-1">Loading every source into one Qlik app</h3>
          <p className="text-sm text-ipc-ink mb-1">
            These paths close a loop, so Qlik reports a circular reference when all of their sources are in one data
            model. Load one subject area per app, or leave one source of each loop out.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-ipc-ink">
            {schema.circularPaths.map((path) => (
              <li key={path.join('>')}>{[...path, path[0]].join(' → ')}</li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

// Every field, schema v1 name to schema v2 name, one collapsible table per
// source.
function FieldMapping({ schema }) {
  const s = schema.summary;
  return (
    <SectionCard id="field-mapping" title={`Field mapping (schema v1 to v${schema.version})`} className="scroll-mt-16 lg:scroll-mt-4" bodyClassName="space-y-3">
      <p className="text-sm text-ipc-ink">
        {s.total} fields: {s.renamed} renamed, {s.unchanged} unchanged, {s.new} new, {s.removed} removed. Use it to
        update a load script, or to rename the fields of files written before schema v{schema.version}.
      </p>
      <div className="divide-y divide-ipc-border border-y border-ipc-border">
        {schema.mapping.map((m) => (
          <details key={m.key} className="py-1.5">
            <summary className="cursor-pointer text-sm text-ipc-ink">
              {m.label} <span className="text-ipc-muted">(entity <Mono>{m.entity}</Mono>)</span>
            </summary>
            <div className="relative mt-2 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-ipc-muted border-b border-ipc-border">
                    <th scope="col" className="py-1 pr-4 font-medium">Schema v1 (before)</th>
                    <th scope="col" className="py-1 pr-4 font-medium">Schema v{schema.version}</th>
                    <th scope="col" className="py-1 font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {m.rows.map((r) => (
                    <tr key={`${r.legacy}-${r.name}`} className="border-b border-ipc-border">
                      <td className="py-1 pr-4">{r.legacy ? <Mono>{r.legacy}</Mono> : <span className="text-ipc-muted">none</span>}</td>
                      <td className="py-1 pr-4">{r.name ? <Mono>{r.name}</Mono> : <span className="text-ipc-muted">none</span>}</td>
                      <td className="py-1 text-ipc-ink">{CHANGE_LABEL[r.change]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ))}
      </div>
    </SectionCard>
  );
}

// The first thing a developer reads: the path pattern, the two file kinds,
// which sources need a license, and the Qlik connection every example uses.
function HowToRead({ overview: o, bucket }) {
  return (
    <Card id="how-to-read" title="How to read this data">
      <dl className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
        <Pair term="Bucket and prefix">
          <Mono>{o.pathPattern}</Mono>
          <span className="block text-ipc-muted mt-0.5">
            The bucket is {bucket.known ? <Mono>{bucket.name}</Mono> : <>named <Mono>{bucket.namePattern}</Mono></>}.
            Each source has its own prefix, for example <Mono>{o.prefixExample}</Mono>.
          </span>
        </Pair>
        <Pair term="Dated files">
          <Mono>{o.datedFiles.pattern}</Mono>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {o.datedFiles.modes.map((m) => (
              <li key={m.label}>{m.label}: {m.dateInName}</li>
            ))}
          </ul>
        </Pair>
        <Pair term="_latest.json">
          <Mono>{o.latest.pattern}</Mono>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {o.latest.points.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </Pair>
        <Pair term="Licenses">
          <ul className="space-y-1">
            {o.licenses.groups.map((g) => (
              <li key={g.level}>
                <span className="badge-amber mr-2">{g.label}</span>
                {g.sources.map((src, i) => (
                  <span key={src.key}>
                    {i > 0 && ', '}
                    <a href={`#${src.anchor}`} className="text-ipc-light underline hover:no-underline">{src.label}</a>
                  </span>
                ))}
              </li>
            ))}
          </ul>
          <span className="block text-ipc-muted mt-1">{o.licenses.note}</span>
        </Pair>
        <Pair term="Qlik connection">
          <Mono>{o.qlik.connectionName}</Mono>
          <span className="block text-ipc-muted mt-0.5">{o.qlik.note}</span>
        </Pair>
      </dl>
    </Card>
  );
}
